import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import FiIcons from '../fiIconMap';
import LayoutDefault from '../layouts/LayoutDefault';
import RouteSeo from '../../../seo/RouteSeo';
import { MARKETING_PAGE_META } from '../../../data/marketingPageMeta';
import '../assets/scss/style.scss';

// Use-case cards for the /use-cases hub. Designed to grow — see
// docs/USE_CASES_AND_FOR_PAGES_PLAN.md for the full planned set (voice notes,
// life admin, personal inbox already exist as standalone pages; more may be
// promoted onto this hub over time).
const USE_CASE_CARDS = [
    {
        path: '/use-cases/second-brain',
        title: 'Second brain',
        description:
            'Capture everything to one inbox first, let Luckynote enrich it, and search it back out. No framework to learn or maintain.'
    },
    {
        path: '/use-cases/read-it-later',
        title: 'Read it later',
        description:
            'A home for the save-now-read-later habit Pocket left behind, with previews, summaries, and real search.'
    },
    {
        path: '/use-cases/screenshot-organization',
        title: 'Screenshot organization',
        description:
            'OCR reads the text inside every screenshot you save, so you can search by what it says, not what you called it.'
    },
    {
        path: '/use-cases/life-admin',
        title: 'Life admin',
        description:
            'Keep receipts, confirmations, forms, deadlines, and personal logistics in one searchable inbox instead of across your camera roll and email.'
    },
    {
        path: '/use-cases/personal-inbox',
        title: 'Personal inbox',
        description:
            'One place for the mixed bag of links, screenshots, notes, tasks, and reminders that do not fit neatly anywhere else.'
    },
    {
        path: '/use-cases/file-transfer',
        title: 'File transfer',
        description:
            'Send files, photos, links, and voice notes from one device, then open the same searchable inbox on the next one.'
    },
    {
        path: '/use-cases/note-to-self',
        title: 'Note to self',
        description:
            'Tell future-you the thought, name, number, or book title before it disappears, then find it on any device.'
    },
    {
        path: '/use-cases/copy-paste-between-devices',
        title: 'Copy and paste between devices',
        description:
            'Move chosen text between your phone and computer, then keep yesterday\'s snippet searchable instead of losing it to the clipboard.'
    },
    {
        path: '/use-cases/recipes',
        title: 'Recipes',
        description:
            'Save recipe reels, screenshots, links, and grocery reminders so the dinner idea is still findable when you want to cook it.'
    },
    {
        path: '/use-cases/travel',
        title: 'Travel planning',
        description:
            'Keep trip ideas, confirmations, screenshots, notes, and reminders together so the useful detail is there when the trip is happening.'
    },
    {
        path: '/use-cases/house-hunting',
        title: 'House hunting',
        description:
            'Save listing links, viewing photos, notes, measurements, and follow-ups in one searchable place while the search is moving quickly.'
    },
    {
        path: '/use-cases/watchlist',
        title: 'Watchlist',
        description:
            'Save films, books, shows, and recommendations now, then find the right one when you are ready to choose.'
    },
    {
        path: '/use-cases/gift-ideas',
        title: 'Gift ideas',
        description:
            'Keep product links, screenshots, and passing hints, then let a reminder bring the right idea back before the occasion.'
    },
    {
        path: '/use-cases/job-search',
        title: 'Job search',
        description:
            'Keep listings, interview debriefs, recruiter contacts, and follow-up reminders together while your search is moving fast.'
    },
    {
        path: '/use-cases/home-projects',
        title: 'Home projects',
        description:
            'Save measurements, paint codes, contractor quotes, and material links so the detail is there when you need it.'
    },
    {
        path: '/use-cases/event-planning',
        title: 'Event planning',
        description:
            'Keep venue photos, vendor quotes, call notes, and deadline reminders in one searchable place while you plan.'
    },
    {
        path: '/use-cases/journaling',
        title: 'Journaling',
        description:
            'Daily notes, transcribed voice memos, and photos in one searchable thread over time. Ask what you were worried about in March.'
    },
    {
        path: '/use-cases/brain-dump',
        title: 'Brain dump',
        description:
            'Empty your head with text, voice, photos, or screenshots. Keep the useful next steps and let the rest go.'
    },
    {
        path: '/use-cases/reading-and-learning',
        title: 'Reading and learning',
        description:
            'Save articles and links, get Luckynote summaries, and search everything you have ever read. A library, not just a queue.'
    },
    {
        path: '/use-cases/competitive-research',
        title: 'Competitive research',
        description:
            'Screenshot competitors’ pricing and pages. OCR makes them searchable, so you can track changes over time.'
    },
    {
        path: '/use-cases/why-people-text-themselves',
        title: 'Why people text themselves',
        description:
            'The fast note-to-self habit explained, plus a better way to find the links, screenshots, and ideas you save.'
    },
    {
        path: '/use-cases/best-apps-to-message-yourself',
        title: 'Best apps to message yourself',
        description:
            'Choose between a self-chat, a notes app, and a capture-first personal inbox based on what you need later.'
    },
    {
        path: '/use-cases/send-notes-from-phone-to-computer',
        title: 'Phone to computer notes',
        description:
            'Save a note, link, screenshot, or file on your phone and retrieve it from the computer where you need it.'
    },
    {
        path: '/use-cases/organize-saved-links-without-folders',
        title: 'Organize links without folders',
        description:
            'Save articles, videos, posts, and products without maintaining a folder system before every capture.'
    },
    {
        path: '/use-cases/save-instagram-and-tiktok-ideas',
        title: 'Save social ideas',
        description:
            'Keep recipes, places, products, and creative references from Instagram and TikTok findable later.'
    },
    {
        path: '/use-cases/search-text-in-screenshots',
        title: 'Search text in screenshots',
        description:
            'Let screenshot text become searchable so a saved image does not disappear into a camera-roll archive.'
    },
    {
        path: '/use-cases/whatsapp-transcriber',
        title: 'Transcriber for WhatsApp',
        description: 'Send WhatsApp voice messages to Luckynote, get them transcribed, and search the text later.'
    },
    {
        path: '/use-cases/telegram-transcriber',
        title: 'Transcriber for Telegram',
        description: 'Send Telegram voice messages to Luckynote and turn them into searchable text.'
    },
    {
        path: '/use-cases/voice-note-transcription',
        title: 'Voice-note transcription',
        description:
            'Turn a personal voice thought into searchable text without losing the original audio.'
    },
    {
        path: '/use-cases/save-web-pages-with-context',
        title: 'Save web pages with context',
        description:
            'Keep the page and the reason you cared together, so an old bookmark still makes sense later.'
    },
    {
        path: '/use-cases/turn-saved-notes-into-reminders',
        title: 'Turn notes into reminders',
        description:
            'Keep the saved note, link, screenshot, or file beside the task and reminder it creates.'
    },
    {
        path: '/use-cases/cross-device-note-capture',
        title: 'Cross-device note capture',
        description:
            'Capture from phone, web, desktop, or browser, then find the same personal inbox from the next screen.'
    }
];

// Newest lastmod first. Missing lastmod always sorts last. Equal dates keep
// their existing relative order (Array.prototype.sort is stable), preserving
// the curated USE_CASE_CARDS ordering as a tie-break.
const byLastmodDesc = (a, b) => {
    if (!a.lastmod && !b.lastmod) return 0;
    if (!a.lastmod) return 1;
    if (!b.lastmod) return -1;
    if (a.lastmod > b.lastmod) return -1;
    if (a.lastmod < b.lastmod) return 1;
    return 0;
};

const UseCasesPage = () => {
    const useCases = USE_CASE_CARDS.map((useCase) => {
        const page = MARKETING_PAGE_META.find((candidate) => candidate.path === useCase.path);
        return {
            ...useCase,
            icon: page?.icon,
            color: page?.iconColor,
            background: page?.iconBackground,
            lastmod: page?.lastmod
        };
    }).filter((useCase) => useCase.icon)
        .sort(byLastmodDesc);

    return (
        <>
            <RouteSeo
                title="Luckynote Use Cases | Luckynote"
                description="See the workflows Luckynote handles best, from building a second brain to replacing read-it-later apps and making screenshots searchable with OCR."
                canonical="https://luckynote.io/use-cases"
            />
            <LayoutDefault>
                <DarkSection>
                    <PageShell>
                        <HeroSection>
                            <HeroTitle>What people use Luckynote for</HeroTitle>
                            <HeroDescription>
                                Same product, different jobs. Pick the workflow that matches what you are trying
                                to do, and see exactly how Luckynote handles it.
                            </HeroDescription>
                        </HeroSection>

                        <CardGrid>
                            {useCases.map((useCase) => {
                                const IconComponent = FiIcons[useCase.icon];
                                return (
                                    <UseCaseCard key={useCase.path} to={useCase.path}>
                                        <IconBadge $color={useCase.color} $background={useCase.background}>
                                            <IconComponent size={22} />
                                        </IconBadge>
                                        <CardTitle>{useCase.title}</CardTitle>
                                        <CardBody>{useCase.description}</CardBody>
                                        <CardCta>
                                            See how it works
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </CardCta>
                                    </UseCaseCard>
                                );
                            })}
                        </CardGrid>

                        <FooterNote>
                            Deciding between apps instead? <Link to="/compare">See how Luckynote compares</Link>.
                            Or find your persona on the <Link to="/for">who it&apos;s for</Link> page.
                        </FooterNote>
                    </PageShell>
                </DarkSection>
            </LayoutDefault>
        </>
    );
};

export default UseCasesPage;

const PAPER = '#fcf9f7';
const PAPER_DEEP = '#f6efe7';

const DarkSection = styled.div`
    background:
        radial-gradient(circle at 14% 4%, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0) 26rem),
        linear-gradient(180deg, ${PAPER} 0%, ${PAPER_DEEP} 56%, ${PAPER} 100%);
    min-height: 100vh;
`;

const PageShell = styled.div`
    max-width: 1120px;
    margin: 0 auto;
    padding: 96px 20px 96px;
`;

const HeroSection = styled.section`
    padding: 0 0 56px;
    text-align: center;
`;

const HeroTitle = styled.h1`
    margin: 0 0 16px;
    font-size: clamp(2rem, 4vw, 3.2rem);
    line-height: 1.1;
    letter-spacing: -0.02em;
    font-weight: 700;
    color: #14110f;
`;

const HeroDescription = styled.p`
    margin: 0 auto;
    font-size: 1.1rem;
    line-height: 1.7;
    color: rgba(20, 17, 15, 0.66);
    max-width: 38rem;
`;

const CardGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 20px;
`;

const UseCaseCard = styled(Link)`
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 28px 26px;
    border-radius: 14px;
    border: 1px solid rgba(20, 17, 15, 0.12);
    background: rgba(255, 255, 255, 0.58);
    text-decoration: none;
    backdrop-filter: blur(12px);
    transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
        background: rgba(255, 255, 255, 0.88);
        border-color: rgba(20, 17, 15, 0.22);
        box-shadow: 0 18px 42px rgba(59, 46, 35, 0.16);
        transform: translateY(-3px);
    }
`;

const IconBadge = styled.div`
    width: 44px;
    height: 44px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${({ $background }) => $background};
    color: ${({ $color }) => $color};
    position: relative;

    img,
    svg {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }
`;

const CardTitle = styled.h3`
    margin: 0;
    font-size: 1.15rem;
    color: #14110f;
`;

const CardBody = styled.p`
    margin: 0;
    font-size: 0.95rem;
    line-height: 1.65;
    color: rgba(20, 17, 15, 0.62);
    flex-grow: 1;
`;

const CardCta = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.9rem;
    font-weight: 600;
    color: #2b88d9;

    svg {
        transition: transform 0.15s ease;
    }

    ${UseCaseCard}:hover & svg {
        transform: translateX(3px);
    }
`;

const FooterNote = styled.p`
    margin: 48px auto 0;
    text-align: center;
    font-size: 0.95rem;
    color: rgba(20, 17, 15, 0.62);

    a {
        color: #2b88d9;
        font-weight: 600;
    }
`;
